/**
 * The 194 competing nations: the 193 UN member states plus the Holy See
 * (Vatican City), which is the roster the WHO also uses.
 *
 * Each record is a compact tuple to keep the table readable and diff-friendly:
 *
 *   [ ISO 3166-1 alpha-2, display name, continent, primary colour, accent colour ]
 *
 * The two colours are taken from the nation's flag and drive the procedural
 * fallback texture, the card rim light, the particle bursts and the score bars,
 * so they must stay in sync with `public/flags/<code>.svg`.
 *
 * No nation carries a hand-authored strength. Opening ratings are derived from a
 * deterministic hash of the country code (see `seedRating`) so that day one is
 * statistically fair, and every rating afterwards is earned from results.
 */
import type { ContinentId, Country } from './types.js';

type CountryRow = readonly [string, string, ContinentId, string, string];

const ROWS: readonly CountryRow[] = [
  // ---------------------------------------------------------------- Africa (54)
  ['DZ', 'Algeria', 'AF', '#006233', '#D21034'],
  ['AO', 'Angola', 'AF', '#CE1126', '#F9D616'],
  ['BJ', 'Benin', 'AF', '#008751', '#E8112D'],
  ['BW', 'Botswana', 'AF', '#75AADB', '#000000'],
  ['BF', 'Burkina Faso', 'AF', '#EF2B2D', '#009E49'],
  ['BI', 'Burundi', 'AF', '#CE1126', '#1EB53A'],
  ['CV', 'Cabo Verde', 'AF', '#003893', '#CF2027'],
  ['CM', 'Cameroon', 'AF', '#007A5E', '#CE1126'],
  ['CF', 'Central African Republic', 'AF', '#003082', '#FFCE00'],
  ['TD', 'Chad', 'AF', '#002664', '#C60C30'],
  ['KM', 'Comoros', 'AF', '#3A75C4', '#3D8E33'],
  ['CG', 'Congo', 'AF', '#009543', '#FBDE4A'],
  ['CD', 'DR Congo', 'AF', '#007FFF', '#F7D618'],
  ['DJ', 'Djibouti', 'AF', '#6AB2E7', '#12AD2B'],
  ['EG', 'Egypt', 'AF', '#CE1126', '#C09300'],
  ['GQ', 'Equatorial Guinea', 'AF', '#3E9A00', '#0073CE'],
  ['ER', 'Eritrea', 'AF', '#EA0437', '#12AD2B'],
  ['SZ', 'Eswatini', 'AF', '#3E5EB9', '#B10C0C'],
  ['ET', 'Ethiopia', 'AF', '#078930', '#FCDD09'],
  ['GA', 'Gabon', 'AF', '#009E60', '#FCD116'],
  ['GM', 'Gambia', 'AF', '#CE1126', '#0C1C8C'],
  ['GH', 'Ghana', 'AF', '#CE1126', '#FCD116'],
  ['GN', 'Guinea', 'AF', '#CE1126', '#FCD116'],
  ['GW', 'Guinea-Bissau', 'AF', '#CE1126', '#FCD116'],
  ['CI', "Cote d'Ivoire", 'AF', '#F77F00', '#009E60'],
  ['KE', 'Kenya', 'AF', '#BB0000', '#006600'],
  ['LS', 'Lesotho', 'AF', '#00209F', '#009543'],
  ['LR', 'Liberia', 'AF', '#002B7F', '#BF0A30'],
  ['LY', 'Libya', 'AF', '#239E46', '#E70013'],
  ['MG', 'Madagascar', 'AF', '#FC3D32', '#007E3A'],
  ['MW', 'Malawi', 'AF', '#21873B', '#CE1126'],
  ['ML', 'Mali', 'AF', '#14B53A', '#FCD116'],
  ['MR', 'Mauritania', 'AF', '#006233', '#FFC400'],
  ['MU', 'Mauritius', 'AF', '#EA2839', '#1A206D'],
  ['MA', 'Morocco', 'AF', '#C1272D', '#006233'],
  ['MZ', 'Mozambique', 'AF', '#009639', '#FFD100'],
  ['NA', 'Namibia', 'AF', '#003580', '#009543'],
  ['NE', 'Niger', 'AF', '#0DB02B', '#E05206'],
  ['NG', 'Nigeria', 'AF', '#008751', '#FFFFFF'],
  ['RW', 'Rwanda', 'AF', '#00A1DE', '#FAD201'],
  ['ST', 'Sao Tome and Principe', 'AF', '#12AD2B', '#FFCE00'],
  ['SN', 'Senegal', 'AF', '#00853F', '#FDEF42'],
  ['SC', 'Seychelles', 'AF', '#003F87', '#D62828'],
  ['SL', 'Sierra Leone', 'AF', '#1EB53A', '#0072C6'],
  ['SO', 'Somalia', 'AF', '#4189DD', '#FFFFFF'],
  ['ZA', 'South Africa', 'AF', '#007A4D', '#FFB612'],
  ['SS', 'South Sudan', 'AF', '#078930', '#DA121A'],
  ['SD', 'Sudan', 'AF', '#D21034', '#007229'],
  ['TZ', 'Tanzania', 'AF', '#1EB53A', '#00A3DD'],
  ['TG', 'Togo', 'AF', '#006A4E', '#FFCE00'],
  ['TN', 'Tunisia', 'AF', '#E70013', '#FFFFFF'],
  ['UG', 'Uganda', 'AF', '#FCDC04', '#D90000'],
  ['ZM', 'Zambia', 'AF', '#198A00', '#EF7D00'],
  ['ZW', 'Zimbabwe', 'AF', '#319208', '#FFD200'],

  // ------------------------------------------------------------------ Asia (47)
  ['AF', 'Afghanistan', 'AS', '#000000', '#D32011'],
  ['AM', 'Armenia', 'AS', '#D90012', '#F2A800'],
  ['AZ', 'Azerbaijan', 'AS', '#00B5E2', '#EF3340'],
  ['BH', 'Bahrain', 'AS', '#DA291C', '#FFFFFF'],
  ['BD', 'Bangladesh', 'AS', '#006A4E', '#F42A41'],
  ['BT', 'Bhutan', 'AS', '#FFD520', '#FF4E12'],
  ['BN', 'Brunei', 'AS', '#F7E017', '#000000'],
  ['KH', 'Cambodia', 'AS', '#032EA1', '#E00025'],
  ['CN', 'China', 'AS', '#EE1C25', '#FFFF00'],
  ['CY', 'Cyprus', 'AS', '#D57800', '#4E5B31'],
  ['GE', 'Georgia', 'AS', '#FF0000', '#FFFFFF'],
  ['IN', 'India', 'AS', '#FF9933', '#138808'],
  ['ID', 'Indonesia', 'AS', '#FF0000', '#FFFFFF'],
  ['IR', 'Iran', 'AS', '#239F40', '#DA0000'],
  ['IQ', 'Iraq', 'AS', '#CE1126', '#007A3D'],
  ['IL', 'Israel', 'AS', '#0038B8', '#FFFFFF'],
  ['JP', 'Japan', 'AS', '#BC002D', '#FFFFFF'],
  ['JO', 'Jordan', 'AS', '#007A3D', '#CE1126'],
  ['KZ', 'Kazakhstan', 'AS', '#00AFCA', '#FEC50C'],
  ['KW', 'Kuwait', 'AS', '#007A3D', '#CE1126'],
  ['KG', 'Kyrgyzstan', 'AS', '#E8112D', '#FFEF00'],
  ['LA', 'Laos', 'AS', '#002868', '#CE1126'],
  ['LB', 'Lebanon', 'AS', '#ED1C24', '#00A651'],
  ['MY', 'Malaysia', 'AS', '#CC0001', '#010066'],
  ['MV', 'Maldives', 'AS', '#D21034', '#007E3A'],
  ['MN', 'Mongolia', 'AS', '#C4272F', '#F9CF02'],
  ['MM', 'Myanmar', 'AS', '#FECB00', '#34B233'],
  ['NP', 'Nepal', 'AS', '#DC143C', '#003893'],
  ['KP', 'North Korea', 'AS', '#024FA2', '#ED1C27'],
  ['OM', 'Oman', 'AS', '#DB161B', '#008000'],
  ['PK', 'Pakistan', 'AS', '#01411C', '#FFFFFF'],
  ['PH', 'Philippines', 'AS', '#0038A8', '#CE1126'],
  ['QA', 'Qatar', 'AS', '#8A1538', '#FFFFFF'],
  ['SA', 'Saudi Arabia', 'AS', '#006C35', '#FFFFFF'],
  ['SG', 'Singapore', 'AS', '#EF3340', '#FFFFFF'],
  ['KR', 'South Korea', 'AS', '#CD2E3A', '#0047A0'],
  ['LK', 'Sri Lanka', 'AS', '#8D2029', '#FFBE29'],
  ['SY', 'Syria', 'AS', '#CE1126', '#007A3D'],
  ['TJ', 'Tajikistan', 'AS', '#CC0000', '#006600'],
  ['TH', 'Thailand', 'AS', '#A51931', '#2D2A4A'],
  ['TL', 'Timor-Leste', 'AS', '#DC241F', '#FFC726'],
  ['TR', 'Turkiye', 'AS', '#E30A17', '#FFFFFF'],
  ['TM', 'Turkmenistan', 'AS', '#28AE66', '#FFFFFF'],
  ['AE', 'United Arab Emirates', 'AS', '#00732F', '#FF0000'],
  ['UZ', 'Uzbekistan', 'AS', '#1EB53A', '#0099B5'],
  ['VN', 'Vietnam', 'AS', '#DA251D', '#FFFF00'],
  ['YE', 'Yemen', 'AS', '#CE1126', '#000000'],

  // ---------------------------------------------------------------- Europe (44)
  ['AL', 'Albania', 'EU', '#E41E20', '#000000'],
  ['AD', 'Andorra', 'EU', '#10069F', '#FEDD00'],
  ['AT', 'Austria', 'EU', '#ED2939', '#FFFFFF'],
  ['BY', 'Belarus', 'EU', '#CE1720', '#007C30'],
  ['BE', 'Belgium', 'EU', '#FDDA24', '#EF3340'],
  ['BA', 'Bosnia and Herzegovina', 'EU', '#002F6C', '#FECB00'],
  ['BG', 'Bulgaria', 'EU', '#00966E', '#D62612'],
  ['HR', 'Croatia', 'EU', '#FF0000', '#171796'],
  ['CZ', 'Czechia', 'EU', '#11457E', '#D7141A'],
  ['DK', 'Denmark', 'EU', '#C60C30', '#FFFFFF'],
  ['EE', 'Estonia', 'EU', '#0072CE', '#000000'],
  ['FI', 'Finland', 'EU', '#003580', '#FFFFFF'],
  ['FR', 'France', 'EU', '#0055A4', '#EF4135'],
  ['DE', 'Germany', 'EU', '#DD0000', '#FFCE00'],
  ['GR', 'Greece', 'EU', '#0D5EAF', '#FFFFFF'],
  ['HU', 'Hungary', 'EU', '#CE2939', '#477050'],
  ['IS', 'Iceland', 'EU', '#02529C', '#DC1E35'],
  ['IE', 'Ireland', 'EU', '#169B62', '#FF883E'],
  ['IT', 'Italy', 'EU', '#008C45', '#CD212A'],
  ['LV', 'Latvia', 'EU', '#9E3039', '#FFFFFF'],
  ['LI', 'Liechtenstein', 'EU', '#002B7F', '#CE1126'],
  ['LT', 'Lithuania', 'EU', '#FDB913', '#006A44'],
  ['LU', 'Luxembourg', 'EU', '#ED2939', '#00A1DE'],
  ['MT', 'Malta', 'EU', '#CF142B', '#FFFFFF'],
  ['MD', 'Moldova', 'EU', '#0046AE', '#FFD200'],
  ['MC', 'Monaco', 'EU', '#CE1126', '#FFFFFF'],
  ['ME', 'Montenegro', 'EU', '#C40308', '#D4AF3A'],
  ['NL', 'Netherlands', 'EU', '#21468B', '#AE1C28'],
  ['MK', 'North Macedonia', 'EU', '#D20000', '#FFE600'],
  ['NO', 'Norway', 'EU', '#BA0C2F', '#00205B'],
  ['PL', 'Poland', 'EU', '#DC143C', '#FFFFFF'],
  ['PT', 'Portugal', 'EU', '#046A38', '#DA291C'],
  ['RO', 'Romania', 'EU', '#002B7F', '#FCD116'],
  ['RU', 'Russia', 'EU', '#0039A6', '#D52B1E'],
  ['SM', 'San Marino', 'EU', '#5EB6E4', '#FFFFFF'],
  ['RS', 'Serbia', 'EU', '#C6363C', '#0C4076'],
  ['SK', 'Slovakia', 'EU', '#0B4EA2', '#EE1C25'],
  ['SI', 'Slovenia', 'EU', '#005DA4', '#ED1C24'],
  ['ES', 'Spain', 'EU', '#AA151B', '#F1BF00'],
  ['SE', 'Sweden', 'EU', '#006AA7', '#FECC00'],
  ['CH', 'Switzerland', 'EU', '#FF0000', '#FFFFFF'],
  ['UA', 'Ukraine', 'EU', '#0057B7', '#FFD700'],
  ['GB', 'United Kingdom', 'EU', '#012169', '#C8102E'],
  ['VA', 'Vatican City', 'EU', '#FFE000', '#FFFFFF'],

  // --------------------------------------------------------- North America (23)
  ['AG', 'Antigua and Barbuda', 'NA', '#CE1126', '#0072C6'],
  ['BS', 'Bahamas', 'NA', '#00778B', '#FFC72C'],
  ['BB', 'Barbados', 'NA', '#00267F', '#FFC726'],
  ['BZ', 'Belize', 'NA', '#003F87', '#CE1126'],
  ['CA', 'Canada', 'NA', '#FF0000', '#FFFFFF'],
  ['CR', 'Costa Rica', 'NA', '#002B7F', '#CE1126'],
  ['CU', 'Cuba', 'NA', '#002A8F', '#CF142B'],
  ['DM', 'Dominica', 'NA', '#006B3F', '#FFCE00'],
  ['DO', 'Dominican Republic', 'NA', '#002D62', '#CE1126'],
  ['SV', 'El Salvador', 'NA', '#0F47AF', '#FFFFFF'],
  ['GD', 'Grenada', 'NA', '#CE1126', '#FCD116'],
  ['GT', 'Guatemala', 'NA', '#4997D0', '#FFFFFF'],
  ['HT', 'Haiti', 'NA', '#00209F', '#D21034'],
  ['HN', 'Honduras', 'NA', '#0073CF', '#FFFFFF'],
  ['JM', 'Jamaica', 'NA', '#009B3A', '#FED100'],
  ['MX', 'Mexico', 'NA', '#006847', '#CE1126'],
  ['NI', 'Nicaragua', 'NA', '#0067C6', '#FFFFFF'],
  ['PA', 'Panama', 'NA', '#005293', '#DA121A'],
  ['KN', 'Saint Kitts and Nevis', 'NA', '#009E49', '#FFD100'],
  ['LC', 'Saint Lucia', 'NA', '#6CCEDD', '#FCD116'],
  ['VC', 'Saint Vincent and the Grenadines', 'NA', '#0072C6', '#FCD116'],
  ['TT', 'Trinidad and Tobago', 'NA', '#CE1126', '#000000'],
  ['US', 'United States', 'NA', '#3C3B6E', '#B22234'],

  // --------------------------------------------------------- South America (12)
  ['AR', 'Argentina', 'SA', '#74ACDF', '#F6B40E'],
  ['BO', 'Bolivia', 'SA', '#D52B1E', '#F9E300'],
  ['BR', 'Brazil', 'SA', '#009C3B', '#FFDF00'],
  ['CL', 'Chile', 'SA', '#0039A6', '#D52B1E'],
  ['CO', 'Colombia', 'SA', '#FCD116', '#003893'],
  ['EC', 'Ecuador', 'SA', '#FFDD00', '#034EA2'],
  ['GY', 'Guyana', 'SA', '#009E49', '#FCD116'],
  ['PY', 'Paraguay', 'SA', '#D52B1E', '#0038A8'],
  ['PE', 'Peru', 'SA', '#D91023', '#FFFFFF'],
  ['SR', 'Suriname', 'SA', '#377E3F', '#B40A2D'],
  ['UY', 'Uruguay', 'SA', '#0038A8', '#FCD116'],
  ['VE', 'Venezuela', 'SA', '#FFCC00', '#00247D'],

  // --------------------------------------------------------------- Oceania (14)
  ['AU', 'Australia', 'OC', '#012169', '#E4002B'],
  ['FJ', 'Fiji', 'OC', '#68BFE5', '#012169'],
  ['KI', 'Kiribati', 'OC', '#CE1126', '#003F87'],
  ['MH', 'Marshall Islands', 'OC', '#003893', '#DD7500'],
  ['FM', 'Micronesia', 'OC', '#75B2DD', '#FFFFFF'],
  ['NR', 'Nauru', 'OC', '#002B7F', '#FFC61E'],
  ['NZ', 'New Zealand', 'OC', '#00247D', '#CC142B'],
  ['PW', 'Palau', 'OC', '#4AADD6', '#FFDE00'],
  ['PG', 'Papua New Guinea', 'OC', '#CE1126', '#000000'],
  ['WS', 'Samoa', 'OC', '#CE1126', '#002B7F'],
  ['SB', 'Solomon Islands', 'OC', '#0051BA', '#215B33'],
  ['TO', 'Tonga', 'OC', '#C10000', '#FFFFFF'],
  ['TV', 'Tuvalu', 'OC', '#5B97B1', '#FFD100'],
  ['VU', 'Vanuatu', 'OC', '#D21034', '#009543'],
];

/** Total number of competitors in a season. */
export const ROSTER_SIZE = 194;

export interface ContinentMeta {
  readonly id: ContinentId;
  readonly name: string;
  /** Broadcast tagline used by the continent introduction scene. */
  readonly tagline: string;
  /** Accent colour of the continent's lower-third and particle burst. */
  readonly color: string;
  /** Longitude the animated globe rotates to when this continent is on screen. */
  readonly longitude: number;
  readonly latitude: number;
}

export const CONTINENTS: readonly ContinentMeta[] = [
  {
    id: 'EU',
    name: 'Europe',
    tagline: 'Old world, modern fire',
    color: '#4DA3FF',
    longitude: 10,
    latitude: 50,
  },
  {
    id: 'AS',
    name: 'Asia',
    tagline: 'The largest arena on Earth',
    color: '#FFB347',
    longitude: 100,
    latitude: 34,
  },
  {
    id: 'AF',
    name: 'Africa',
    tagline: 'Where the heartbeat starts',
    color: '#3BD16F',
    longitude: 20,
    latitude: 2,
  },
  {
    id: 'NA',
    name: 'North America',
    tagline: 'Bright lights, big stage',
    color: '#FF5F6D',
    longitude: -100,
    latitude: 45,
  },
  {
    id: 'SA',
    name: 'South America',
    tagline: 'Rhythm, flair and no fear',
    color: '#FFD93D',
    longitude: -60,
    latitude: -15,
  },
  {
    id: 'OC',
    name: 'Oceania',
    tagline: 'Out of the blue',
    color: '#5FE3D0',
    longitude: 150,
    latitude: -25,
  },
];

const CONTINENT_BY_ID = new Map(CONTINENTS.map((c) => [c.id, c]));

export function continentMeta(id: ContinentId): ContinentMeta {
  const meta = CONTINENT_BY_ID.get(id);
  if (!meta) throw new Error(`Unknown continent: ${id}`);
  return meta;
}

/**
 * FNV-1a. Small, fast, stable across runs and platforms - which matters because
 * the opening ratings must be identical on the server, in the tests and in any
 * future rebuild of the database.
 */
function fnv1a(input: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash >>> 0;
}

/**
 * Opening rating for a nation, in the 1380-1620 band around the 1500 anchor.
 * Derived purely from the country code so that no nation is favoured by hand,
 * yet the field is not perfectly flat (a flat field makes every match a coin
 * flip, which reads as noise on screen).
 */
export function seedRating(code: string): number {
  const h = fnv1a(`fbwl:rating:${code}`);
  return 1380 + (h % 241);
}

export const COUNTRIES: readonly Country[] = ROWS.map(
  ([code, name, continent, primaryColor, accentColor]) => ({
    code,
    name,
    continent,
    primaryColor,
    accentColor,
    rating: seedRating(code),
  }),
);

const COUNTRY_BY_CODE = new Map(COUNTRIES.map((c) => [c.code, c]));

export function countryByCode(code: string): Country | undefined {
  return COUNTRY_BY_CODE.get(code);
}

export function countriesOfContinent(id: ContinentId): readonly Country[] {
  return COUNTRIES.filter((c) => c.continent === id);
}
